import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { CheckCircle2, Clock, Play, Plus, Table2 } from "lucide-react";
import type { ColumnConfig, AuditFlowDocument, TabularCell } from "../shared/types";
import { TabularCell as TabularCellComponent } from "./TabularCell";
import { TREditColumnMenu } from "./TREditColumnMenu";
import { useColumnResize } from "@/app/hooks/useColumnResize";

const SKELETON_COLS = 4;
const SKELETON_ROWS = 5;

const COL_W = "w-[300px] shrink-0";
const CHECK_W = "w-8 shrink-0";
const STATUS_COL_W = "w-[160px] shrink-0";

// Pixel widths matching the CSS constants above
const CHECK_W_PX = 32;   // w-8 = 2rem = 32px
const DEFAULT_DOC_W = 300;
const DEFAULT_STATUS_W = 160;
const DEFAULT_DATA_W = 300;

type DocStatus = "Ready to Run" | "Not Reviewed" | "Reviewed";

/** Derive document status from its cells + whether the user marked it reviewed. */
function deriveStatus(
    docId: string,
    cells: TabularCell[],
    reviewedDocIds: Set<string>,
    hasColumns: boolean,
): DocStatus {
    if (reviewedDocIds.has(docId)) return "Reviewed";
    if (!hasColumns) return "Ready to Run";
    const docCells = cells.filter((c) => c.document_id === docId);
    if (
        docCells.length > 0 &&
        docCells.every((c) => c.status === "done" && c.content != null)
    ) {
        return "Not Reviewed";
    }
    return "Ready to Run";
}

function StatusBadge({
    status,
    onToggle,
}: {
    status: DocStatus;
    onToggle: () => void;
}) {
    if (status === "Ready to Run") {
        return (
            <span
                title="Click Run to generate results for this document"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 select-none"
            >
                <Play className="h-2.5 w-2.5 fill-blue-700" />
                Ready to Run
            </span>
        );
    }
    if (status === "Not Reviewed") {
        return (
            <button
                onClick={onToggle}
                title="Click to mark as Reviewed"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
            >
                <Clock className="h-2.5 w-2.5" />
                Not Reviewed
            </button>
        );
    }
    return (
        <button
            onClick={onToggle}
            title="Click to undo review"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
        >
            <CheckCircle2 className="h-2.5 w-2.5" />
            Reviewed
        </button>
    );
}

/** A thin drag handle at the right edge of a header cell. */
function ResizeHandle({
    onMouseDown,
}: {
    onMouseDown: (e: React.MouseEvent) => void;
}) {
    return (
        <div
            className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10"
            onMouseDown={onMouseDown}
        />
    );
}

export interface TRTableHandle {
    scrollToCell: (colIdx: number, rowIdx: number) => void;
}

interface Props {
    reviewId: string;
    loading: boolean;
    columns: ColumnConfig[];
    documents: AuditFlowDocument[];
    cells: TabularCell[];
    savingColumn: boolean;
    savingColumnsConfig: boolean;
    selectedDocIds: string[];
    selectedColumnIndices: number[];
    reviewedDocIds: Set<string>;
    highlightedCell?: { colIdx: number; rowIdx: number } | null;
    onSelectionChange: (ids: string[]) => void;
    onColumnSelectionChange: (indices: number[]) => void;
    onToggleReviewed: (docId: string) => void;
    onExpand: (cell: TabularCell) => void;
    onCitationClick: (cell: TabularCell, page: number, quote: string) => void;
    onUpdateColumn: (col: ColumnConfig) => void;
    onDeleteColumn: (colIndex: number) => void;
    onRunColumn?: (columnIndex: number) => void;
    runDisabled?: boolean;
    onAddColumn: () => void;
    onAddDocuments: () => void;
}

export const TRTable = forwardRef<TRTableHandle, Props>(function TRTable(
    {
        reviewId,
        loading,
        columns,
        documents,
        cells,
        savingColumn,
        savingColumnsConfig,
        selectedDocIds,
        selectedColumnIndices,
        reviewedDocIds,
        highlightedCell,
        onSelectionChange,
        onColumnSelectionChange,
        onToggleReviewed,
        onExpand,
        onCitationClick,
        onUpdateColumn,
        onDeleteColumn,
        onRunColumn,
        runDisabled,
        onAddColumn,
        onAddDocuments,
    },
    ref,
) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sortedColumns = [...columns].sort((a, b) => a.index - b.index);

    // Build defaults: fixed columns + one entry per data column
    const colDefaults: Record<string, number> = {
        doc: DEFAULT_DOC_W,
        status: DEFAULT_STATUS_W,
    };
    for (const col of sortedColumns) {
        colDefaults[`col_${col.index}`] = DEFAULT_DATA_W;
    }

    const { widths, setWidths, startResize, w } = useColumnResize(
        `tr_col_widths_${reviewId}`,
        colDefaults,
    );

    // When new columns are added, ensure they get a default width entry
    useEffect(() => {
        setWidths((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const col of sortedColumns) {
                const key = `col_${col.index}`;
                if (!(key in next)) {
                    next[key] = DEFAULT_DATA_W;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);

    // Derived pixel values from current widths
    const docW = w("doc");
    const statusW = w("status");
    const statusStickyLeft = CHECK_W_PX + docW;
    const totalContentWidth =
        CHECK_W_PX +
        docW +
        statusW +
        sortedColumns.reduce((sum, col) => sum + w(`col_${col.index}`), 0) +
        32; // trailing spacer

    useImperativeHandle(ref, () => ({
        scrollToCell(colIdx: number, rowIdx: number) {
            const container = scrollContainerRef.current;
            if (!container) return;

            // Vertical: find actual row via DOM (handles variable row heights)
            const allRows = container.querySelectorAll<HTMLElement>(
                ":scope > div.flex.min-w-full",
            );
            const targetRow = allRows[rowIdx];
            if (targetRow) {
                container.scrollTo({
                    top: Math.max(0, targetRow.offsetTop - 40),
                    behavior: "smooth",
                });
            }

            // Horizontal: sum per-column widths to center target column
            const stickyLeftPx = CHECK_W_PX + docW + statusW;
            const colOffset = sortedColumns
                .slice(0, colIdx)
                .reduce((sum, col) => sum + w(`col_${col.index}`), 0);
            const colW = w(`col_${sortedColumns[colIdx]?.index ?? 0}`);
            const targetScrollLeft =
                stickyLeftPx + colOffset - container.clientWidth / 2 + colW / 2;
            container.scrollLeft = Math.max(0, targetScrollLeft);
        },
    }));

    function getCell(docId: string, colIdx: number) {
        return cells.find(
            (c) => c.document_id === docId && c.column_index === colIdx,
        );
    }

    const allSelected =
        documents.length > 0 &&
        documents.every((d) => selectedDocIds.includes(d.id));
    const someSelected =
        !allSelected && documents.some((d) => selectedDocIds.includes(d.id));

    function toggleAll() {
        if (allSelected) {
            onSelectionChange([]);
        } else {
            onSelectionChange(documents.map((d) => d.id));
        }
    }

    function toggleDoc(id: string) {
        if (selectedDocIds.includes(id)) {
            onSelectionChange(selectedDocIds.filter((x) => x !== id));
        } else {
            onSelectionChange([...selectedDocIds, id]);
        }
    }

    const allColumnsSelected =
        sortedColumns.length > 0 &&
        sortedColumns.every((col) => selectedColumnIndices.includes(col.index));
    const someColumnsSelected =
        !allColumnsSelected &&
        sortedColumns.some((col) => selectedColumnIndices.includes(col.index));

    function toggleAllColumns() {
        if (allColumnsSelected) {
            onColumnSelectionChange([]);
        } else {
            onColumnSelectionChange(sortedColumns.map((col) => col.index));
        }
    }

    function toggleColumn(columnIndex: number) {
        if (selectedColumnIndices.includes(columnIndex)) {
            onColumnSelectionChange(
                selectedColumnIndices.filter((idx) => idx !== columnIndex),
            );
        } else {
            onColumnSelectionChange([...selectedColumnIndices, columnIndex]);
        }
    }

    if (loading) {
        return (
            <div className="flex-1 overflow-hidden">
                {/* Header */}
                <div className="flex border-b border-gray-200">
                    <div
                        className={`${CHECK_W} border-r border-gray-200 p-2`}
                    />
                    <div
                        className={`${COL_W} border-r border-gray-200 p-2 text-xs font-medium text-gray-500`}
                    >
                        Document
                    </div>
                    <div
                        className={`${STATUS_COL_W} border-r border-gray-200 p-2 text-xs font-medium text-gray-500`}
                    >
                        Status
                    </div>
                    {Array.from({ length: SKELETON_COLS }).map((_, i) => (
                        <div
                            key={i}
                            className={`${COL_W} border-r border-gray-200 p-2`}
                        >
                            <div className="h-4 w-28 rounded bg-gray-100 animate-pulse" />
                        </div>
                    ))}
                    <div className="flex-1" />
                </div>
                {/* Rows */}
                {Array.from({ length: SKELETON_ROWS }).map((_, row) => (
                    <div
                        key={row}
                        className={`flex border-b border-gray-50 ${row % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                    >
                        <div className={`${CHECK_W} p-2`} />
                        <div className={`${COL_W} p-2`}>
                            <div className="h-4 w-32 rounded bg-gray-100 animate-pulse" />
                        </div>
                        <div className={`${STATUS_COL_W} p-2`}>
                            <div className="h-4 w-20 rounded-full bg-gray-100 animate-pulse" />
                        </div>
                        {Array.from({ length: SKELETON_COLS }).map((_, col) => (
                            <div key={col} className={`${COL_W} p-2`}>
                                <div className="h-4 rounded bg-gray-100 animate-pulse" />
                            </div>
                        ))}
                        <div className="flex-1" />
                    </div>
                ))}
            </div>
        );
    }

    if (columns.length === 0 && documents.length === 0) {
        return (
            <div className="flex flex-1 flex-col overflow-hidden">
                <div className="flex items-center border-b border-gray-200">
                    <div className={`${CHECK_W} border-r border-gray-200`} />
                    <div
                        className={`${COL_W} border-r border-gray-200 p-2 text-xs font-medium text-gray-500 select-none`}
                    >
                        Document
                    </div>
                    <div className="flex-1" />
                </div>
                <div className="flex flex-1 flex-col items-start justify-center w-full max-w-xs mx-auto">
                    <Table2 className="h-8 w-8 text-gray-300 mb-4" />
                    <p className="text-2xl font-medium font-serif text-gray-900">
                        Tabular Review
                    </p>
                    <p className="mt-1 text-xs text-gray-400 text-left">
                        Add columns and documents to get started.
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                        <button
                            onClick={onAddColumn}
                            className="inline-flex items-center gap-1 rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-gray-700 shadow-md"
                        >
                            + Add Columns
                        </button>
                        <button
                            onClick={onAddDocuments}
                            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            <Plus className="h-3.5 w-3.5" />
                            Add Documents
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-auto" ref={scrollContainerRef}>
            {/* Header */}
            <div
                className="sticky top-0 z-20 flex bg-white h-8"
                style={{ minWidth: totalContentWidth }}
            >
                {/* Checkbox column — fixed, non-resizable */}
                <div
                    className={`sticky left-0 z-30 ${CHECK_W} bg-white border-b border-r border-gray-200 flex justify-center items-center select-none`}
                >
                    <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                        }}
                        onChange={toggleAll}
                        className="h-2.5 w-2.5 rounded border-gray-200 cursor-pointer accent-black"
                    />
                </div>

                {/* Document column — resizable, sticky */}
                <div
                    className="sticky left-8 z-30 shrink-0 relative bg-white border-b border-r border-gray-200 p-2 text-left text-xs font-medium text-gray-500 select-none"
                    style={{ width: docW }}
                >
                    Document
                    <ResizeHandle onMouseDown={(e) => startResize("doc", e)} />
                </div>

                {/* Status column — resizable, sticky */}
                <div
                    className="sticky z-30 shrink-0 relative bg-white border-b border-r border-gray-200 p-2 text-left text-xs font-medium text-gray-500 select-none"
                    style={{ width: statusW, left: statusStickyLeft }}
                >
                    Status
                    <ResizeHandle onMouseDown={(e) => startResize("status", e)} />
                </div>

                {/* Data columns — resizable, not sticky */}
                {sortedColumns.map((col) => (
                    <div
                        key={col.index}
                        className={`shrink-0 relative border-b border-r border-gray-200 p-2 text-left text-xs font-medium text-gray-500 select-none ${
                            selectedColumnIndices.includes(col.index)
                                ? "bg-blue-50"
                                : ""
                        }`}
                        style={{ width: w(`col_${col.index}`) }}
                    >
                        <div className="flex items-center gap-1.5 pr-1 min-w-0">
                            <input
                                type="checkbox"
                                checked={selectedColumnIndices.includes(col.index)}
                                onChange={() => toggleColumn(col.index)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-2.5 w-2.5 shrink-0 rounded border-gray-200 cursor-pointer accent-black"
                            />
                            <span className="truncate flex-1 min-w-0">{col.name}</span>
                            <TREditColumnMenu
                                column={col}
                                disabled={savingColumn || savingColumnsConfig}
                                onSave={onUpdateColumn}
                                onDelete={onDeleteColumn}
                                onRunColumn={
                                    onRunColumn
                                        ? () => onRunColumn(col.index)
                                        : undefined
                                }
                                runDisabled={runDisabled}
                            />
                        </div>
                        <ResizeHandle onMouseDown={(e) => startResize(`col_${col.index}`, e)} />
                    </div>
                ))}

                {/* Trailing spacer + add column button */}
                <div className="flex-1 border-b border-gray-200 flex items-center justify-start gap-2 p-2 min-w-8">
                    {sortedColumns.length > 0 && (
                        <input
                            type="checkbox"
                            checked={allColumnsSelected}
                            ref={(el) => {
                                if (el) el.indeterminate = someColumnsSelected;
                            }}
                            onChange={toggleAllColumns}
                            title="Select all columns"
                            className="h-2.5 w-2.5 shrink-0 rounded border-gray-200 cursor-pointer accent-black"
                        />
                    )}
                    <button
                        onClick={onAddColumn}
                        disabled={savingColumn || savingColumnsConfig}
                        className="flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors disabled:text-gray-200"
                    >
                        <Plus className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Rows */}
            {documents.map((doc, docIdx) => {
                const rowBg = selectedDocIds.includes(doc.id)
                    ? "bg-gray-100"
                    : docIdx % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50";
                return (
                    <div
                        key={doc.id}
                        className={`flex ${rowBg}`}
                        style={{ minWidth: totalContentWidth }}
                    >
                        {/* Checkbox */}
                        <div
                            className={`sticky left-0 z-[60] ${CHECK_W} border-b border-r border-gray-200 p-2 flex items-center justify-center ${rowBg}`}
                        >
                            <input
                                type="checkbox"
                                checked={selectedDocIds.includes(doc.id)}
                                onChange={() => toggleDoc(doc.id)}
                                className="h-2.5 w-2.5 shrink-0 rounded border-gray-200 cursor-pointer accent-black"
                            />
                        </div>

                        {/* Document name */}
                        <div
                            className={`sticky left-8 z-[60] shrink-0 border-b border-r border-gray-200 p-2 text-xs text-gray-800 flex items-center ${rowBg}`}
                            style={{ width: docW }}
                        >
                            <span className="line-clamp-1" title={doc.filename}>
                                {doc.filename}
                            </span>
                        </div>

                        {/* Status */}
                        <div
                            className={`sticky z-[60] shrink-0 border-b border-r border-gray-200 p-2 flex items-center ${rowBg}`}
                            style={{ width: statusW, left: statusStickyLeft }}
                        >
                            <StatusBadge
                                status={deriveStatus(doc.id, cells, reviewedDocIds, columns.length > 0)}
                                onToggle={() => onToggleReviewed(doc.id)}
                            />
                        </div>

                        {/* Data cells */}
                        {sortedColumns.map((col) => {
                            const cell = getCell(doc.id, col.index);
                            const colPos = sortedColumns.findIndex(
                                (c) => c.index === col.index,
                            );
                            const isHighlighted =
                                highlightedCell?.colIdx === colPos &&
                                highlightedCell?.rowIdx === docIdx;
                            return (
                                <div
                                    key={col.index}
                                    className={`shrink-0 border-b border-r border-gray-200 transition-colors ${isHighlighted ? "bg-blue-200" : ""}`}
                                    style={{ width: w(`col_${col.index}`) }}
                                >
                                    {cell && (
                                        <TabularCellComponent
                                            cell={cell}
                                            column={col}
                                            onExpand={() => onExpand(cell)}
                                            onCitationClick={(page, quote) =>
                                                onCitationClick(
                                                    cell,
                                                    page,
                                                    quote,
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            );
                        })}
                        <div className="flex-1 border-b border-gray-200 min-h-8 min-w-8" />
                    </div>
                );
            })}
        </div>
    );
});
